import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FirebaseResponseCode } from '../firebase';
import { useLocation, useNavigate } from 'react-router-dom';
import LoginButton from '../components/LoginButton';
import { ROUTES } from '../routes';
import { sanitizeAndValidateEmail } from '../utils/validation';

const Login = () => {
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const navigate = useNavigate();
    const {
        currentUser,
        login,
    } = useAuth();

    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    // Bezpečné odvození returnUrl (jen interní cesty, ne /login)
    const rawReturnUrl = searchParams.get('returnUrl') || ROUTES.HOME;
    const isAbsolute = /^https?:\/\//i.test(rawReturnUrl);
    const isLoginPath = rawReturnUrl === ROUTES.LOGIN;
    const safeReturnUrl = (!isAbsolute && !isLoginPath) ? rawReturnUrl : ROUTES.HOME;

    useEffect(() => {
        // Pokud je uživatel již přihlášený a náhodou zůstane na /login, pošleme ho pryč,
        // ale vyhneme se slučce – použijeme replace a jen pokud jsme skutečně na /login.
        if (currentUser && location.pathname === ROUTES.LOGIN) {
            // Nevolat navigate dokud login submit neprobíhá – necháme to řešit handleSubmit tvrdým reloadem.
            // Tím zamezíme dvojímu přesměrování v rychlém sledu.
        }
    }, [currentUser, location.pathname]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        setError('');
        setLoading(true);

        const email = emailRef.current?.value || '';
        const password = passwordRef.current?.value || '';

        const emailValidation = sanitizeAndValidateEmail(email);
        if (!emailValidation.isValid) {
            setError(emailValidation.error || 'Invalid email');
            setLoading(false);
            return;
        }
        if (!password || password.trim().length === 0) {
            setError('Password is required');
            setLoading(false);
            return;
        }
        if (password.length < 6) {
            setError('Password is too short');
            setLoading(false);
            return;
        }

        try {
            const response = await login(emailValidation.sanitized, password);

            if (response.code === FirebaseResponseCode.OK) {
                // Po loginu vynutíme refresh tokenu pro spolehlivější stav na iOS/Safari
                try {
                    const token = await response.user?.getIdToken(true);
                    // console.log('Token:', token);
                } catch (_e) {
                    // ignore, redirect stejně proběhne
                }
                // Tvrdý reload na cílovou URL zabrání závodu mezi onAuthStateChanged a guardy
                window.location.replace(safeReturnUrl);
                return; // Zajistit, že dál nic neběží
            } else {
                setError(response.message);
            }
        } catch (error) {
            setError('Login failed. Please try again.');
        }

        setLoading(false);
    }

    return (
        <div className="login-container">
            <h2>Login to GDS Archive</h2>
            <form className="login-form" onSubmit={handleSubmit}>
                <div className="input-group">
                    <label htmlFor="email">Email</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        ref={emailRef}
                        required
                        maxLength={254}
                        autoComplete="email"
                        disabled={loading}
                    />
                </div>
                <div className="input-group">
                    <label htmlFor="password">Password</label>
                    <input
                        type="password"
                        id="password"
                        name="password"
                        ref={passwordRef}
                        required
                        minLength={6}
                        autoComplete="current-password"
                        disabled={loading}
                    />
                </div>
                {error && <div style={{ color: 'red', margin: '10px 0' }}>{error}</div>}
                <LoginButton onClick={() => {}}></LoginButton>
                <button type="button" onClick={() => navigate(ROUTES.FORGOTTEN_PASSWORD)}>Forgot your password?</button>
            </form>
        </div>
    );
};

export default Login;
